import type { ButtonHTMLAttributes } from 'react';
import styles from './LangToggle.module.css';

export type Lang = 'EN' | 'FR';

export interface LangToggleProps extends Omit<ButtonHTMLAttributes<HTMLDivElement>, 'onChange'> {
  value: Lang;
  onChange: (next: Lang) => void;
  /** Accessible label for the group. Default: "Language". */
  ariaLabel?: string;
}

/**
 * Two-option EN/FR language pill. Renders in the top-right of the auth form
 * panel. The caller is responsible for persisting the choice (cookie +
 * <html lang>) — this component is purely controlled.
 */
export function LangToggle({
  value,
  onChange,
  ariaLabel = 'Language',
  className,
  ...rest
}: LangToggleProps) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={[styles.root, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {(['EN', 'FR'] as const).map((lang) => {
        const active = value === lang;
        return (
          <button
            key={lang}
            type="button"
            className={[styles.option, active ? styles.optionActive : null]
              .filter(Boolean)
              .join(' ')}
            aria-pressed={active}
            onClick={() => onChange(lang)}
          >
            {lang}
          </button>
        );
      })}
    </div>
  );
}
